/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, takeEvery, call, put, select,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import memberDucks from '../../../Member/ducks';
import { getOrderId } from '../../../Checkout/ducks/Common/Common-Selectors';
import {
    loadCart, mfeGetCart, callHandleCartFailure, updateCartSnapShot,
    callPrepareCartCompleted, callPrepareCart,
} from './Cart-Actions';
import { retrieveCart } from './ducks/RetrieveCart/RetrieveCart-Operations';
import { loadCartCount } from '../../../Order/Order-Actions';
import cartServiceClient from '../../../../../apis/cart-service-apis/cartServiceClient';
import {
    clearAuthDependentState,
} from '../../../Member/ducks/Auth/Auth-Actions';
import takeLeading from '../../../../../app/helpers/sagas/takeLeading';
import { getFeatureFlags } from '../App/App-Selectors';
import { processFeatureErrors } from '../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../Checkout/ducks/Common/ducks/Error/Error-Actions';

const {
    auth: {
        authOperations,
    },
} = memberDucks;

const { checkJWT } = authOperations.workers;

/**
 * Changes the order of the cart (Last In Last Out)
 * the last item added to cart is the last item
 * @param {object} cart
 * @returns {object} reversed cart
 */
function reverseCart(cart) {
    if (cart?.recipients?.length > 0) {
        const cartToReverse = { ...cart };

        const recipients = cartToReverse.recipients.reverse();

        recipients.forEach((orderItem, index) => {
            recipients[index].cartItems = orderItem.cartItems.reverse();
        });

        cartToReverse.recipients = recipients;

        return cartToReverse;
    }

    return cart;
}

function* workerLoadCart(action) {
    const flagState = yield select(getFeatureFlags);
    const cart = JSON.parse(JSON.stringify(action.data || {}));
    const cartId = yield select(getOrderId);

    yield put(clearOrderItemErrors(
        cartId,
        'giftCard',
        { errType: 'clearAllFields', field: '' },
    ));
    if (flagState['is-cart-reverse-enabled']) {
        const cartReversed = yield call(reverseCart, cart);

        yield put(updateCartSnapShot(cartReversed));
    } else {
        yield put(updateCartSnapShot(cart));
    }
}

function* watchLoadCart() {
    yield takeLeading(loadCart().type, workerLoadCart);
}

/**
 * Handle Cart Failure
 * Cart Failure occurs when stale OrderId is used.
 * Try to rehydraid session from localstorage or redirect to homepage
*/
export function* handleCartFailure(action) {
    const logOrderId = yield select(getOrderId);
    const { error } = action.data;
    let errorKey = '';

    if (error?.response?.data?.detailedError?.errorKey) {
        errorKey = error?.response?.data?.detailedError?.errorKey;
    } else if (error?.response?.data?.errorKey) {
        errorKey = error?.response?.data?.errorKey;
    }

    if (
        errorKey === 'ERROR_NOT_AUTHORIZED_TO_ACCESS_CART'
        || errorKey === 'ERROR_INVALID_CART_STATE'
        || (
            error
            && error.response
            && error.response.status
            && ['401', '403'].includes(error.response.status.toString())
        )
    ) {
        mbpLogger.logError({
            function: 'handleCartFailure',
            appName: process.env.npm_package_name,
            module: 'Cart-Operations.js',
            message: `Error - ${errorKey}`,
            orderId: logOrderId,
        });

        yield put(clearAuthDependentState(true));
        yield call(checkJWT);
        yield put(loadCartCount(0));
        yield put(loadCart({}));
    } else if (errorKey === 'ERROR_CARTITEM_NOT_FOUND') {
        mbpLogger.logError({
            function: 'handleCartFailure',
            appName: process.env.npm_package_name,
            module: 'Cart-Operations.js',
            message: `Cart Item Not Found - ${logOrderId} - Refreshing the Cart`,
            orderId: logOrderId,
        });

        yield call(retrieveCart);
    }
}

function* watchCartRetrieve() {
    yield takeLeading(mfeGetCart().type, retrieveCart);
}

function* watcherHandleCartFailure() {
    yield takeEvery(callHandleCartFailure().type, handleCartFailure);
}

export function* workerPrepareCart() {
    const orderId = yield select(getOrderId);

    try {
        if (orderId) {
            const featureFlags = yield select(getFeatureFlags);
            // Clean the error in shipping if it come with errors from review page
            // if the flag is on mean we are showing the errors on the review page
            // if the flag is off mean we are redirecting from review page to shipping page
            if (featureFlags['is-item-errors-show-on-review-page-enabled']) {
                yield put(clearOrderItemErrors(
                    orderId,
                    'cart',
                    { errType: 'clearAllFields', field: '' },
                ));
                yield put(clearOrderItemErrors(
                    orderId,
                    'payment',
                    { errType: 'clearAllFields', field: '' },
                ));
            }

            const jwtToken = yield call(checkJWT);
            yield call(cartServiceClient.prepareCart, { env: {}, jwtToken, cartId: orderId });
        } else {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                function: 'workerPrepareCart',
                message: 'Order Id is undefined to call prepare cart',
                orderId,
            });
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerPrepareCart',
            jsError: ex,
            message: 'Error occured while prepare',
            orderId,
            errorValues: ex.response?.data,
        });

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: orderId,
                errorFeatureType: 'cart',
                errorValues: ex.response?.data,
            },
        });
    }

    yield put(callPrepareCartCompleted());
}

function* watcherPrepareCart() {
    yield takeEvery(callPrepareCart().type, workerPrepareCart);
}

const watchers = [
    fork(watchCartRetrieve),
    fork(watcherHandleCartFailure),
    // fork(watcherGetCartInSession), // removing this is not need in checkout since tthe api already call the cart
    fork(watchLoadCart),
    fork(watcherPrepareCart),
];

export {
    watchers,
    // Actions
    loadCart,
    mfeGetCart,
};
